import ErrorBoundary from "components/Errors/ErrorBoundary";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/Layouts/DashboardLayout";
import api from "lib/api";
import { Button, Select, TextInput } from "@alm/flow-ds/components";
import { useDispatch } from "react-redux";
import { newForm, statusReady } from "data/form/formActions";
import Form from "components/Forms/Default";
import { useHistory, useLocation } from "react-router-dom";
import { Workbench } from "components/Workbench";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";
import { useTranslation } from "react-i18next";

// const elements: QueryByFormInputTypes[] = [
// 	{
// 		id: "BA",
// 		label: "Title",
// 		elem: "text",
// 		name: "BA",
// 		uuid: "",
// 	},
// 	{
// 		id: "di",
// 		label: "Created",
// 		elem: "date",
// 		name: "di",
// 		uuid: "",
// 	},
// 	{
// 		id: "ni",
// 		label: "Role",
// 		elem: "text",
// 		name: "ni",
// 		uuid: "",
// 	},
// 	{
// 		id: "cat",
// 		label: "Category",
// 		elem: "text",
// 		name: "cat",
// 		uuid: "",
// 	},
// 	{
// 		id: "role-id",
// 		label: "Department",
// 		elem: "select",
// 		name: "department",
// 		uuid: "",
// 		options: ["dep a", "dep b", "dep c"],
// 	},
// 	{
// 		id: "looky-a",
// 		label: "Multi Lookup",
// 		elem: "lookup",
// 		name: "looky-a",
// 		uuid: "",
// 		isArray: true,
// 		search: {
// 			columns: [],
// 			payload: {
// 				filter: { AND: [] },
// 				sort: [],
// 				"page-size": 10,
// 				"request-uri": "cdp:/xplus/resources/collect",
// 			},
// 			view: { elements: [] },
// 			label: "/BA",
// 		},
// 	},
// 	{
// 		id: "looky-b",
// 		label: "Single Lookup",
// 		elem: "lookup",
// 		name: "looky-b",
// 		uuid: "",
// 		isArray: false,
// 		search: {
// 			columns: [],
// 			payload: {
// 				filter: { AND: [] },
// 				sort: [],
// 				"page-size": 10,
// 				"request-uri": "cdp:/xplus/resources/collect",
// 			},
// 			view: { elements: [] },
// 			label: "/BA",
// 		},
// 	},
// ];

// const testData: SearchConfig = {
// 	columns: [
// 		{ id: "BA", label: "Title" },
// 		{ id: "VV", label: "Name" },
// 		{ id: "VW", label: "Age" },
// 	],
// 	view: {
// 		elements: elements,
// 	},
// 	payload: {
// 		filter: {
// 			AND: [],
// 		},
// 		sort: [],
// 		"page-size": 10,
// 		"request-uri": "cdp:/xplus/resources/collect",
// 	},
// 	label: "",
// };

interface Definition {
	id: string;
	formLabel: string;
}

function TaskPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();

	const [definitions, setDefinitions] = useState<Definition[]>([]);

	const [fid, setFid] = useState("");
	const [resource, setResource] = useState<string | undefined>(undefined);
	const [activeRecordId, setActiveRecordId] = useState<undefined | string>(
		undefined
	);
	const [activeDefinition, setActiveDefinition] = useState<
		undefined | string
	>(undefined);

	const queryParams = useMemo(
		() => new URLSearchParams(location?.search),
		[location]
	);

	const TENANT: string = useMemo(
		() =>
			window.location.hostname.substring(
				0,
				window.location.hostname.indexOf(".")
			) || "",
		[]
	);

	const dispatchError = useCallback(
		error => {
			dispatch(
				createAlertAction(
					createAlert({
						title: t(`alerts.${error}.title`),
						description: t(`alerts.${error}.description`, {
							formDefValue: activeDefinition,
						}),
						status: "error",
					})
				)
			);
		},
		[activeDefinition, dispatch, t]
	);

	const getResource = useCallback(
		async definition => {
			const definitionId = definition.substring(
				definition.lastIndexOf("/") + 1
			);

			try {
				const res = await api.forms.getFormDefinition(definitionId);
				const resource = res.data.view.resource;
				setResource(resource.substring(resource.lastIndexOf("/") + 1));
			} catch (error) {
				dispatchError("errorGettingResource");
			}
		},
		[dispatchError]
	);

	const onFormDefinitionChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			setActiveDefinition(event.currentTarget.value);
			getResource(event.currentTarget.value);
		},
		[getResource]
	);

	// Load the form definition and if there is a recordId load the record,
	// otherwise create a new instance
	const loadForm = useCallback(async () => {
		if (activeDefinition && resource) {
			try {
				const instance = await api.forms.create(
					activeDefinition,
					activeRecordId && TENANT
						? `cdp:/${TENANT}/${resource}/${activeRecordId}`
						: null
				);
				dispatch(newForm(instance.instanceId, instance));
				dispatch(statusReady(instance.instanceId));
				setFid(instance.instanceId);
				history.push(
					`/playground?definition=${activeDefinition}&recordId=${
						activeRecordId ? activeRecordId : ""
					}`
				);
			} catch (error) {
				dispatchError("formUnfound");
			}
		}
	}, [
		TENANT,
		activeDefinition,
		activeRecordId,
		dispatch,
		dispatchError,
		history,
		resource,
	]);

	const getDefinitions = useCallback(async () => {
		try {
			const res = api.forms.getDefinitions();
			setDefinitions((await res).data.matches);
		} catch (error) {
			dispatchError("errorGettingFormDefintions");
		}
	}, [dispatchError]);

	// If there is a definition or record id in the url we will
	// automatically select the definition from the list and
	// input the record id
	useEffect(() => {
		const definition = queryParams.get("definition")
			? (queryParams.get("definition") as string)
			: "";
		const recordId = queryParams.get("recordId")
			? (queryParams.get("recordId") as string)
			: "";

		if (recordId && !activeRecordId) setActiveRecordId(recordId);
		if (definition && !activeDefinition) {
			setActiveDefinition(definition);
			getResource(definition);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Get list of definitions for the option list
	useEffect(() => {
		getDefinitions();
	}, [getDefinitions, activeDefinition]);

	// HACK: this is just a temporary cb for testing qbf and should not be left in playground
	// const onSelectedCB: AddSelectedCallback = useCallback(selected => {
	// 	console.log(selected);
	// }, []);

	return (
		<DashboardLayout>
			<div className="playground">
				<div className="container-fluid mt-3 playground__form">
					<Select
						label="Select a form definition"
						value={activeDefinition}
						onChange={onFormDefinitionChange}>
						<>
							<option value={undefined}>
								Select a form definition
							</option>
							{definitions &&
								definitions.map(d => (
									<option value={d.id} key={d.id}>
										{d.formLabel}
									</option>
								))}
						</>
					</Select>
					<TextInput
						label="Record ID"
						type="number"
						value={activeRecordId}
						onChange={e => setActiveRecordId(e.currentTarget.value)}
						minNumber={0}
					/>
					<Button
						label="Load form"
						className="btn-primary mb-4"
						onClick={loadForm}
					/>
				</div>
				<Workbench>
					<ErrorBoundary>
						{fid ? <Form fid={fid} workbenchIndex={0} /> : null}
					</ErrorBoundary>
				</Workbench>
			</div>
		</DashboardLayout>
	);
}

export default React.memo(TaskPage);
